import { Fragment, type JSX, type ReactNode } from 'react';

import { humanizeParameterName } from '@heidi/agents/data';
import { typographyVariant } from '@heidi/design-system/typography';
import { cn } from '@heidi/utils';

const PARAM_CHIP_CLASS = cn(
  typographyVariant({ variant: 'captionBold' }),
  'mx-px inline-flex items-center rounded-[5px] px-1.5 py-px align-[0.05em]',
  'min-w-0 max-w-64 truncate',
  // entity-blue (not tier-pro): tier-pro is the Pro-tier billing violet, so
  // using it for fill-in variables read as "premium feature". A fill-in token
  // is the page's single accent — shared with the active-step badge.
  'bg-entity-blue-muted text-entity-blue-foreground ring-1 ring-inset ring-entity-blue-foreground/15'
);

/**
 * A single template fill-in chip. Unresolved parameters display a humanised
 * label (e.g. "Patient name"); callers may provide children for resolved
 * values that should keep the same fill-in-variable treatment.
 */
export function ParamChip({
  name,
  children,
}: {
  readonly name: string;
  readonly children?: ReactNode;
}): JSX.Element {
  const label = children ?? humanizeParameterName(name);
  const title = typeof label === 'string' ? label : undefined;
  return (
    <span className={PARAM_CHIP_CLASS} title={title}>
      {label}
    </span>
  );
}

export type TemplateParameterRenderer = (
  parameterName: string,
  index: number
) => ReactNode | undefined;

export function TemplateParts({
  value,
  renderParameter,
}: {
  readonly value: string;
  readonly renderParameter?: TemplateParameterRenderer;
}): JSX.Element {
  return (
    <>
      {value.split(/\{\{([^}]+)\}\}/).map((segment, i) => {
        if (i % 2 === 1) {
          const parameterName = segment.trim();
          const rendered = renderParameter?.(parameterName, i);
          if (rendered !== undefined) {
            return <Fragment key={`param-${i}`}>{rendered}</Fragment>;
          }
          return <ParamChip key={`param-${i}`} name={parameterName} />;
        }
        return segment ? <span key={`text-${i}`}>{segment}</span> : null;
      })}
    </>
  );
}
